/**
 * Калибровка на старте · логика экрана (12-й модуль витрины с настоящей логикой).
 *
 * Что здесь есть: машина состояний «пачка за пачкой» — какую полосу показать дальше,
 * когда остановиться, кого переспросить, что отправить в ядро.
 * ⛔ Чего здесь НЕТ: решения о судьбе слов. Границу и раскладку считает `core/calibrate.py`
 * из присланных ответов — витрина шлёт только нажатия человека.
 *
 * ⭐ И ни одного своего числа: размер пачки, пороги «вверх/вниз», потолок нажатий и
 * псевдослова приезжают от ядра (`GET /calibration` → `rules`). Копия правила на витрине
 * разъехалась бы молча — на этом проект уже обжигался (`verb_usage`, список оборотов).
 *
 * ⛔ Кнопки «пропустить» нет вовсе (решение владельца 19.08): калибровку проходят все —
 * и новые, и уже зашедшие.
 */

export interface CalWord {
  word_id: number;
  word: string;
  ru: string;
  freq_rank: number | null;
  distractors: string[];
}

export interface CalRules {
  bands: { key: string; title: string }[];
  start_band: number;
  pack_size: number;
  up_known: number;
  down_known: number;
  max_taps: number;
  traps: string[];
}

export interface CalAnswer { word_id: number; band: string; known: boolean }

export type Stage = "probe" | "verify" | "ceiling" | "done";

export interface CalState {
  rules: CalRules;
  words: Record<string, CalWord[]>;
  band: number;                     // индекс текущей полосы
  used: Record<string, number>;     // сколько слов полосы уже показано
  packSeen: number;                 // сколько слов текущей пачки уже отвечено
  packKnown: number;                // из них «знаю»
  answers: CalAnswer[];
  taps: number;
  dir: number;                      // куда шагнули в прошлый раз: +1 вверх, −1 вниз, 0 старт
  stage: Stage;
  trapsShown: number;
  trapFailed: boolean;
  verifyQueue: CalWord[];           // кого переспрашиваем «а правда знаешь?»
  verifyMiss: number;
}

/** ⭐ Сколько «знаю» переспрашиваем. Три — столько же, сколько в спеке 25.07. */
export const VERIFY_MAX = 3;

/** На какой позиции в пачке вклеивается ловушка. Внутрь, а не отдельным экраном:
 *  псевдослово не должно выглядеть экзаменом, иначе человек насторожится. */
const TRAP_SLOT = 2;

export function startState(rules: CalRules, words: Record<string, CalWord[]>): CalState {
  const band = Math.min(Math.max(rules.start_band, 0), rules.bands.length - 1);
  return {
    rules, words, band,
    used: {}, packSeen: 0, packKnown: 0, answers: [], taps: 0, dir: 0,
    stage: "probe", trapsShown: 0, trapFailed: false, verifyQueue: [], verifyMiss: 0,
  };
}

function bandKey(s: CalState, i = s.band): string {
  return s.rules.bands[i]?.key ?? "";
}

function pool(s: CalState, i = s.band): CalWord[] {
  return s.words[bandKey(s, i)] ?? [];
}

/** Слова этой полосы кончились? Тогда лестница дальше в эту сторону не идёт. */
function exhausted(s: CalState, i = s.band): boolean {
  return (s.used[bandKey(s, i)] ?? 0) >= pool(s, i).length;
}

/** Что показать сейчас: слово полосы или вклеенную ловушку. null = показывать нечего. */
export function current(s: CalState): { kind: "word" | "trap"; word: string; item?: CalWord } | null {
  if (s.stage !== "probe") return null;
  // Ловушка встаёт внутрь первых двух пачек — по одной на пачку.
  if (s.trapsShown < s.rules.traps.length && s.packSeen === TRAP_SLOT
      && s.trapsShown === Math.floor(s.taps / Math.max(1, s.rules.pack_size))) {
    return { kind: "trap", word: s.rules.traps[s.trapsShown] };
  }
  const item = pool(s)[s.used[bandKey(s)] ?? 0];
  return item ? { kind: "word", word: item.word, item } : null;
}

/** Куда шагать после законченной пачки. ⭐ Пороги — из ядра, не наши. */
export function judge(known: number, seen: number, rules: CalRules): "up" | "down" | "border" {
  if (!seen) return "border";
  const share = known / seen;
  if (share >= rules.up_known / rules.pack_size) return "up";
  if (share <= rules.down_known / rules.pack_size) return "down";
  return "border";
}

/** Полосы, где человек показал знание, — по его же ответам (для итога и подписи). */
export function borderKey(s: CalState): string {
  const by: Record<string, { seen: number; known: number }> = {};
  for (const a of s.answers) {
    const b = (by[a.band] ??= { seen: 0, known: 0 });
    b.seen += 1;
    if (a.known) b.known += 1;
  }
  const share = s.rules.up_known / s.rules.pack_size;
  const passed = s.rules.bands
    .map((b) => b.key)
    .filter((k) => by[k]?.seen && by[k].known / by[k].seen >= share);
  return passed.length ? passed[passed.length - 1] : "";
}

function toVerify(s: CalState): CalState {
  // Переспрашиваем тех, кого он отметил «знаю»: до VERIFY_MAX слов с вариантами.
  const byId = new Map<number, CalWord>();
  for (const list of Object.values(s.words)) for (const w of list) byId.set(w.word_id, w);
  const queue = s.answers
    .filter((a) => a.known)
    .map((a) => byId.get(a.word_id))
    .filter((w): w is CalWord => !!w && w.distractors.length >= 3)
    .slice(0, VERIFY_MAX);
  return { ...s, stage: queue.length ? "verify" : "done", verifyQueue: queue };
}

/** Ответ на слово или ловушку. Возвращает НОВОЕ состояние (⛔ прежнее не меняем). */
export function answer(s: CalState, known: boolean): CalState {
  if (s.stage !== "probe") return s;
  const cur = current(s);
  if (!cur) return toVerify(s);

  if (cur.kind === "trap") {
    // ⭐ Клюнул на псевдослово → самооценка завышена системно. Ловушка НЕ считается
    // ответом о полосе: слова-то не существует.
    return { ...s, trapsShown: s.trapsShown + 1, taps: s.taps + 1,
             trapFailed: s.trapFailed || known };
  }

  const key = bandKey(s);
  const next: CalState = {
    ...s,
    used: { ...s.used, [key]: (s.used[key] ?? 0) + 1 },
    answers: [...s.answers, { word_id: cur.item!.word_id, band: key, known }],
    packSeen: s.packSeen + 1,
    packKnown: s.packKnown + (known ? 1 : 0),
    taps: s.taps + 1,
  };

  if (next.taps >= next.rules.max_taps) return toVerify(next);     // потолок нажатий
  if (next.packSeen < next.rules.pack_size && !exhausted(next)) return next;

  // Пачка закончилась — решаем, куда дальше.
  const verdict = judge(next.packKnown, next.packSeen, next.rules);
  const fresh = { ...next, packSeen: 0, packKnown: 0 };
  if (verdict === "border") return toVerify(fresh);
  const step = verdict === "up" ? 1 : -1;
  // ⭐ Разворот направления — тоже граница: между двумя полосами не качаемся.
  if (s.dir !== 0 && step !== s.dir) return toVerify(fresh);

  const target = fresh.band + step;
  if (target < 0) return toVerify(fresh);                          // ниже некуда
  if (target >= fresh.rules.bands.length || exhausted(fresh, target)) {
    // Полосы кончились сверху — это «частотное ядро тебе мало», честный экран.
    return verdict === "up" ? { ...fresh, stage: "ceiling" } : toVerify(fresh);
  }
  return { ...fresh, band: target, dir: step };
}

/** Ответ на переспрос «а правда знаешь?». `right` — попал ли в перевод. */
export function verifyAnswer(s: CalState, right: boolean): CalState {
  if (s.stage !== "verify") return s;
  const rest = s.verifyQueue.slice(1);
  return {
    ...s,
    verifyQueue: rest,
    verifyMiss: s.verifyMiss + (right ? 0 : 1),
    stage: rest.length ? "verify" : "done",
  };
}

/** Варианты перевода для переспроса: правильный + три чужих, порядок стабильный. */
export function verifyOptions(w: CalWord): string[] {
  return [w.ru, ...w.distractors.slice(0, 3)];
}

/**
 * ⭐ Строгая трактовка: клюнул на псевдослово ИЛИ промахнулся на переспросе —
 * значит «знаю» ставилось на глазок, и ядру мы про это говорим честно.
 */
export function strict(s: CalState): boolean {
  return s.trapFailed || s.verifyMiss > 0;
}

/** Что отправляем ядру. ⛔ Ни границы, ни судьбы слов — их считает ядро. */
export function payload(s: CalState): { answers: CalAnswer[]; trap_failed: boolean } {
  return { answers: s.answers, trap_failed: strict(s) };
}

/* ── тексты ────────────────────────────────────────────────────────────────── */

export function plural(n: number, one: string, few: string, many: string): string {
  const a = Math.abs(n) % 100;
  const b = a % 10;
  if (a > 10 && a < 20) return many;
  if (b > 1 && b < 5) return few;
  if (b === 1) return one;
  return many;
}

/** Подсказка о том, что происходит. ⛔ Слова «полоса частотности» человек не видит. */
export function hint(s: CalState): string {
  if (s.dir > 0) return "Легко идёт — берём слова посложнее";
  if (s.dir < 0) return "Возьмём попроще";
  return "Отметь слова, которые точно знаешь";
}

/** Сколько шагов позади — для полоски прогресса (0…1). */
export function progress(s: CalState): number {
  if (s.stage === "done" || s.stage === "ceiling") return 1;
  return Math.min(1, s.taps / s.rules.max_taps);
}

export interface CalResult {
  border: string;
  border_title: string;
  known: number;
  fan: number;
  fan_per_day: number;
  fan_days: number;
  opened_bands: number;
  trap_failed: boolean;
}

/** Заголовок итога. ⛔ Уровня CEFR не называем: калибровка ищет границу, а не «B1». */
export function resultTitle(r: CalResult): string {
  if (!r.border) return "Начнём с самого начала";
  return `Твоя граница — ${r.border_title}`;
}

/** Из чего сложится обучение. Каждая строка — цифра ЯДРА, а не наша оценка. */
export function resultLines(r: CalResult): string[] {
  const out: string[] = [];
  if (r.known) {
    out.push(`${r.known} ${plural(r.known, "слово", "слова", "слов")} ты отметил знакомыми — ` +
             "они уйдут на дальние повторы и мешать не будут.");
  }
  if (r.fan) {
    out.push(`${r.fan} ${plural(r.fan, "слово", "слова", "слов")} ниже границы придут ` +
             `на быструю проверку — примерно по ${r.fan_per_day} в день. ` +
             "Знаешь — ответишь за секунду, и слово уйдёт надолго. Не знаешь — вернётся в начало.");
  } else {
    out.push("Пока проверять нечего — начинаем с самых частых слов.");
  }
  if (r.opened_bands) {
    out.push(`Открыли новые наборы слов: ${r.opened_bands}.`);
  }
  if (r.trap_failed) {
    out.push("Пару раз «знаю» стояло на глазок — поэтому такие слова мы всё-таки проверим, " +
             "а не спишем со счетов.");
  }
  return out;
}

/** Честный экран потолка: человек закрыл все полосы. ⛔ Не тупик, а два хода. */
export function ceilingLines(): string[] {
  return [
    "Частотное ядро тебе мало — ты знаешь почти всё, что в нём есть.",
    "Дальше растут не списки, а умение доставать слова из головы: " +
    "приложение будет чаще спрашивать перевод с русского на английский.",
  ];
}
